import { Trash, ShieldCheck, ListChecks, Database, Clock, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const SUPPORT_EMAIL = "suporte@alugaja.app.br";

export default function DataDeletion() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="data-deletion-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#004E98]/10 text-[#004E98] mb-3">
          <Trash size={28} weight="bold" />
        </div>
        <h1 className="text-2xl font-heading font-bold text-[#004E98] mb-2">
          Exclusão de conta e tratamento de dados — AlugaJá
        </h1>
        <p className="text-slate-600 text-sm max-w-xl mx-auto">
          Esta página explica como você, usuário do aplicativo <b>AlugaJá</b>, pode solicitar a
          exclusão da sua conta e dos seus dados pessoais, quais dados são removidos ou mantidos e
          por quanto tempo.
        </p>
      </div>

      {/* Passo a passo */}
      <section className="aj-card bg-white border border-slate-100 shadow-sm rounded-xl p-5 mb-4" data-testid="deletion-steps">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks size={22} weight="bold" className="text-[#004E98]" />
          <h2 className="font-bold text-slate-800 text-base">Como solicitar a exclusão da sua conta</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Você pode excluir sua conta a qualquer momento, diretamente pelo aplicativo:
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
          <li>Abra o app <b>AlugaJá</b> e faça login.</li>
          <li>Acesse <b>Perfil → Editar perfil</b>.</li>
          <li>Role até o final e toque em <b>“Excluir minha conta”</b>.</li>
          <li>Confirme digitando <b>EXCLUIR</b> e toque em confirmar.</li>
        </ol>
        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900 flex gap-2">
          <EnvelopeSimple size={18} weight="bold" className="shrink-0 mt-0.5" />
          <span>
            Não consegue acessar o app? Envie um e-mail para{" "}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Solicitação de exclusão de conta`} className="font-bold underline">
              {SUPPORT_EMAIL}
            </a>{" "}
            com o assunto <b>“Exclusão de conta”</b>, informando o e-mail cadastrado. Concluímos a
            solicitação em até 30 dias.
          </span>
        </div>
      </section>

      {/* Dados excluídos */}
      <section className="aj-card bg-white border border-slate-100 shadow-sm rounded-xl p-5 mb-4" data-testid="deletion-data-removed">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={22} weight="bold" className="text-green-600" />
          <h2 className="font-bold text-slate-800 text-base">Dados que são EXCLUÍDOS / anonimizados</h2>
        </div>
        <p className="text-sm text-slate-600 mb-2">
          Após o período de carência, removemos ou anonimizamos de forma permanente os seus dados
          pessoais, incluindo:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
          <li>Nome completo, e-mail e telefone;</li>
          <li>CPF/CNPJ e documentos de identidade enviados;</li>
          <li>Endereços e dados de localização;</li>
          <li>Foto de perfil;</li>
          <li>Dados de cartão salvos (token de pagamento) e tokens de notificação push;</li>
          <li>Chave PIX e dados de recebimento.</li>
        </ul>
      </section>

      {/* Dados retidos */}
      <section className="aj-card bg-white border border-slate-100 shadow-sm rounded-xl p-5 mb-4" data-testid="deletion-data-retained">
        <div className="flex items-center gap-2 mb-3">
          <Database size={22} weight="bold" className="text-amber-600" />
          <h2 className="font-bold text-slate-800 text-base">Dados que são MANTIDOS (e por quê)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-2">
          Por obrigações legais, fiscais e de prevenção a fraudes, alguns registros são mantidos de
          forma <b>anonimizada</b> (sem identificar você), por até <b>5 anos</b>:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
          <li>Registros de transações financeiras e notas/recibos de locação (exigência fiscal);</li>
          <li>Histórico de repasses e movimentações de carteira;</li>
          <li>Registros mínimos de disputas já resolvidas, para fins legais.</li>
        </ul>
        <p className="text-xs text-slate-500 mt-3">
          Esses dados não permitem mais identificar o titular após a anonimização e são descartados
          ao fim do prazo legal de guarda.
        </p>
      </section>

      {/* Prazos */}
      <section className="aj-card bg-white border border-slate-100 shadow-sm rounded-xl p-5 mb-4" data-testid="deletion-timeline">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={22} weight="bold" className="text-[#004E98]" />
          <h2 className="font-bold text-slate-800 text-base">Prazos de armazenamento</h2>
        </div>
        <ul className="space-y-2 text-sm text-slate-700">
          <li className="flex gap-2"><ArrowRight size={16} weight="bold" className="text-[#004E98] mt-0.5 shrink-0" /> <span><b>Imediato:</b> ao solicitar, sua conta é desativada, seus anúncios saem do ar e você não consegue mais alugar.</span></li>
          <li className="flex gap-2"><ArrowRight size={16} weight="bold" className="text-[#004E98] mt-0.5 shrink-0" /> <span><b>Até 30 dias (carência):</b> período em que a exclusão pode ser revertida pelo suporte, a seu pedido.</span></li>
          <li className="flex gap-2"><ArrowRight size={16} weight="bold" className="text-[#004E98] mt-0.5 shrink-0" /> <span><b>Após 30 dias:</b> os dados pessoais são anonimizados/apagados automaticamente e de forma irreversível.</span></li>
          <li className="flex gap-2"><ArrowRight size={16} weight="bold" className="text-amber-600 mt-0.5 shrink-0" /> <span><b>Disputa em aberto:</b> se houver uma disputa vinculada à sua conta, a exclusão definitiva fica suspensa até a resolução.</span></li>
          <li className="flex gap-2"><ArrowRight size={16} weight="bold" className="text-slate-400 mt-0.5 shrink-0" /> <span><b>Até 5 anos:</b> retenção de registros financeiros anonimizados por obrigação legal/fiscal.</span></li>
        </ul>
      </section>

      <div className="text-center text-sm text-slate-600 mb-2">
        Dúvidas sobre privacidade? Consulte também nossos{" "}
        <Link to="/termos" className="text-[#004E98] font-bold underline">Termos de Uso</Link>.
      </div>
      <div className="mt-4 text-center text-xs text-slate-400 font-medium">
        Última atualização: Junho de 2026. · AlugaJá Intermediação Digital Ltda.
      </div>
    </div>
  );
}
